// sonos.js — client seam for the Sonos Control API proxy (api/sonos).
//
// Mirrors lib/aiden.js: gated on VITE_SONOS_PROXY. When off, listGroups() returns
// [] (so the UI simply shows no Sonos targets) and control calls throw a clear
// error. When on, every call hits the server-side proxy — no Sonos creds or
// tokens ever touch the browser.
//
// The player uses this to become a *remote control* when a Sonos group is the
// active output: transport buttons call these instead of the local <audio>.

import { apiFetch } from "./api-auth.js";

export function sonosProxyEnabled() {
  return import.meta.env.VITE_SONOS_PROXY === "true";
}

async function call(path, { method = "GET", body } = {}) {
  if (!sonosProxyEnabled()) {
    throw new Error("Sonos is not configured (VITE_SONOS_PROXY is off).");
  }
  const res = await apiFetch(`/api/sonos/${path}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Sonos ${method} ${path} failed (${res.status})`);
  return data;
}

// These used to catch everything and return [], which meant a failed request
// was indistinguishable from "you own no speakers". That is exactly how a 401
// hid for hours: the card rendered empty, no error appeared anywhere, and
// nothing retried. An empty array is a legitimate ANSWER; it must not double as
// an error signal.
//
// The proxy being switched off IS a legitimate empty answer, so that still
// returns [] quietly. Anything else throws, and callers decide what to show.

/** List speaker groups (whole-house first). [] when the proxy is off; throws on failure. */
export async function listGroups() {
  if (!sonosProxyEnabled()) return [];
  const { groups } = await call("groups");
  return groups || [];
}

/** Every speaker in the household. [] when the proxy is off; throws on failure. */
export async function listPlayers() {
  if (!sonosProxyEnabled()) return [];
  const { players } = await call("players");
  return players || [];
}

/**
 * Non-throwing variant for surfaces that must not break on a Sonos outage.
 * Returns { groups, error } so the caller can render a message instead of
 * silently showing nothing.
 */
export async function listGroupsSafe() {
  try {
    return { groups: await listGroups(), error: null };
  } catch (e) {
    return { groups: [], error: e?.message || "Couldn't reach Sonos" };
  }
}

/**
 * Bond every speaker into a single group ("all rooms") and return its id.
 * Idempotent: if the household is already one group, that group is returned.
 */
export async function groupAllRooms() {
  const { groupId, playerCount, alreadyGrouped } = await call("group-all", { method: "POST", body: {} });
  return { groupId, playerCount, alreadyGrouped };
}

/** Group every room, then start the stream on it. The "play everywhere" path. */
export async function playEverywhere(streamUrl, title) {
  const { groupId, playerCount } = await groupAllRooms();
  await playOnGroup(groupId, streamUrl, title);
  return { groupId, playerCount };
}

/** Start playing a stream URL on a group (whole-house playback). */
export function playOnGroup(groupId, streamUrl, title) {
  return call("play", { method: "POST", body: { groupId, streamUrl, title } });
}

export function pauseGroup(groupId) {
  return call("pause", { method: "POST", body: { groupId } });
}

export function resumeGroup(groupId) {
  return call("resume", { method: "POST", body: { groupId } });
}

/** Fully stop (suspends the session — reliable teardown, unlike a bare pause). */
export function stopGroup(groupId) {
  return call("stop", { method: "POST", body: { groupId } });
}

export function setGroupVolume(groupId, volume) {
  return call("volume", { method: "POST", body: { groupId, volume } });
}

export function muteGroup(groupId, muted) {
  return call("mute", { method: "POST", body: { groupId, muted } });
}

/** Seek to an absolute position (milliseconds) within the current item. */
export function seekGroup(groupId, positionMillis) {
  return call("seek", { method: "POST", body: { groupId, positionMillis } });
}

/** Poll while a group is the active output to reflect play/pause + position. */
export function getGroupStatus(groupId) {
  return call(`status?groupId=${encodeURIComponent(groupId)}`);
}
